import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/th";
import api from "../../../utils/axios";
import DatePicker from "../../components/DatePicker";
import QuoteModal from "../../components/Modal/Contract/QuoteModal";
import ServiceLocationModal from "../../components/Modal/Quote/Admin/Service2ndModal";
import { getData } from "../../../utils/SamuiUtils";

const normalizeAD = (dateStr) => {
  if (!dateStr) return "";
  const y = Number(dateStr.substring(0, 4));
  if (y > 2400) {
    return moment(dateStr).subtract(543, "year").format("YYYY-MM-DD");
  }
  return moment(dateStr).format("YYYY-MM-DD");
};

const LEGACY_UNIT_MAP = {
  1: "days",
  2: "weeks",
  3: "months",
  4: "years",
  5: "months",
};

const calcEndDate = (start, period, unit, unitMap = {}) => {
  if (!start || !period) return "";
  let end = moment(start);
  const p = Number(period);
  const mappedUnit = unitMap?.[Number(unit)] || LEGACY_UNIT_MAP[Number(unit)] || "months";
  end.add(p, mappedUnit);
  return end.format("YYYY-MM-DD");
};

/** คำนวณวันที่สิ้นสุดสัญญาจากระยะเวลาของใบเสนอราคา แล้ว -1 day */
const calcEndDateFromQuotePeriod = (start, period, unit, unitMap = {}) => {
  const raw = calcEndDate(start, period, unit, unitMap);
  if (!raw) return "";
  return moment(raw).subtract(1, "day").format("YYYY-MM-DD");
};

function formatWithCommas(numStr) {
  if (numStr === null || numStr === undefined || numStr === "") return "";
  const parts = numStr.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
}

const NumberInput = ({ name, label, value, onValueChange, isReadOnly = false }) => {
  const [localValue, setLocalValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      if (value === null || value === undefined || value === "") {
        setLocalValue("");
      } else {
        setLocalValue(formatWithCommas(parseFloat(value).toFixed(2)));
      }
    }
  }, [value]);

  const handleFocus = (e) => { e.target.select(); };

  const handleChange = (e) => {
    let raw = e.target.value.replace(/,/g, "");
    if (!/^\d*\.?\d*$/.test(raw)) return;
    const parts = raw.split(".");
    if (parts[1]?.length > 2) return;
    let displayVal = raw;
    if (parts[0].length > 3) displayVal = formatWithCommas(raw);
    setLocalValue(displayVal);
    const numericValue = parseFloat(raw) || 0;
    onValueChange(name, numericValue);
  };

  const handleBlur = () => {
    const numericValue = parseFloat(localValue.replace(/,/g, "")) || 0;
    const formatted = formatWithCommas(numericValue.toFixed(2));
    setLocalValue(formatted);
    onValueChange(name, numericValue);
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-1 text-left">{label}</label>
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={localValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        className="w-full h-9 border rounded-md px-2 text-[14px] outline-none focus:ring-2 focus:ring-orange-400 text-right"
        inputMode="decimal"
      />
    </div>
  );
};

export default function ContractHeader({
  header,
  setHeader,
  isEdit = false,
  quoteLinkState = null,
  contractId = null, // ส่งเมื่อแก้ไขสัญญา — ใช้ exclude ตอนดึงจุดย่อยที่ใช้แล้ว
  setReload,
  serviceLocations = [],
  contractLocation = null,
  quoteIdFromURL,
  isReadOnly = false,
  title = "รายละเอียดสัญญา",
  showPrintButton = false,
  onPrint,
  onQuoteChange, // callback เมื่อเลือกใบเสนอราคาใหม่
  onEditContractLocation, // callback เปิด modal แก้ไข ข้อมูลที่ใช้ในสัญญา
  onEditServiceLocation,  // callback เปิด modal แก้ไข สถานที่บริการ
}) {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [timeUnitMap, setTimeUnitMap] = useState({});

  const [choosePurpose, setChoosePurpose] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setHeader((prev) => {
      let next = { ...prev, [name]: type === "checkbox" ? (checked ? 1 : 0) : value };
      if (type === "checkbox" && name === "IsVat" && checked) {
        next.IsVatIn = 0;
        // IsVat ใช้ รวมราคา โดยตรง (+7%) — แปลงเฉพาะเมื่อมาจาก IsVatIn (base → รวมราคา)
        if (prev.IsVatIn === 1) {
          const d = Number(prev.Discount ?? prev.Discount_Value) || 0;
          const net = (Number(prev.Total_Price) || 0) - d;
          next.Total_Price = net * 1.07 + d; // base → รวมราคา (ก่อน +7%)
        }
      }
      if (type === "checkbox" && name === "IsVatIn") {
        next.IsVat = checked ? 0 : prev.IsVat;
        const d = Number(prev.Discount ?? prev.Discount_Value) || 0;
        const net = (Number(prev.Total_Price) || 0) - d;
        if (checked) {
          // เปิด IsVatIn — Total_Price ต้องเป็น base (หาร 1.07)
          // จาก no-VAT: net=รวมราคา → base=net/1.07
          // จาก IsVat: net=รวมราคา → ยอดรวม=net*1.07, base=ยอดรวม/1.07=net
          if (prev.IsVat === 0) next.Total_Price = net > 0 ? net / 1.07 + d : 0;
          else next.Total_Price = net + d; // จาก IsVat: รวมราคา = base สำหรับ IsVatIn (net*1.07/1.07=net)
        } else if (next.IsVat === 0) {
          next.Total_Price = net * 1.07 + d; // base → รวมราคา
        }
      }
      return next;
    });
  };

  useEffect(() => {
    if (!quoteIdFromURL) return;
    api.get(`/get-quote/${quoteIdFromURL}`).then((res) => {
      handleSelectQuote(res.data.header, res.data.subLocations);
    });
  }, [quoteIdFromURL]);


  useEffect(() => {
    if (loadingQuote) return;
    // รวมราคา = รวมจากบริการที่หักส่วนลดแล้ว
    // เมื่อ IsVatIn: Total_Price เก็บราคาตั้งต้นก่อน VAT (base)
    const total = Number(header.Total_Price) || 0;
    const discount = Number(header.Discount ?? header.Discount_Value) || 0;
    const net = total - discount;
    let vatAmount = 0;
    let grand = net;

    if (header.IsVatIn === 1) {
      // Total_Price = ราคาตั้งต้น (base) → grand = base * 1.07
      vatAmount = net * 1.07 - net;
      grand = net * 1.07;
    } else if (header.IsVat === 1) {
      vatAmount = net * 0.07;
      grand = net + vatAmount;
    }

    setHeader((prev) => ({
      ...prev,
      NetPrice: header.IsVatIn === 1 ? grand : net, // เมื่อ IsVatIn ให้ Net = ยอดรวม 10,000
      VatAmount: vatAmount,
      GrandTotal: grand,
    }));
  }, [header.Total_Price, header.Discount, header.Discount_Value, header.IsVat, header.IsVatIn, loadingQuote]);

  // ไม่บังคับ IsVat=1 เมื่อมียอด — ให้ผู้ใช้เลือกเอง (ติ๊ก/ไม่ติ๊ก VAT)
  // เดิมมี useEffect บังคับ IsVat ทำให้ยอดท้ายบิลรวม VAT แม้ไม่ติ๊ก → ผลรวมการชำระเงินไม่ตรงกับยอดรวมท้ายบิลตอน Assign Job

  useEffect(() => {
    if (!header.Start_Date) return;
    const period = header.Contract_Period;
    const unit = header.Contract_Unit;
    if (period && (unit != null && unit !== "")) {
      const end = calcEndDateFromQuotePeriod(header.Start_Date, period, unit, timeUnitMap);
      if (end) setHeader((prev) => ({ ...prev, End_Date: end }));
    }
  }, [header.Start_Date, header.Contract_Period, header.Contract_Unit, timeUnitMap]);

  useEffect(() => {
    let isMounted = true;

    const normalizeList = (res) => {
      if (Array.isArray(res)) return res;
      if (Array.isArray(res?.data)) return res.data;
      return [];
    };

    const fetchTimeUnits = async () => {
      try {
        const res = await getData(
          "Tb_Set_TimeUnit",
          "UPPER(TimeUnit_Type) = 'CONTRACT' AND UPPER(TimeUnit_Status) = 'Y'"
        );
        const list = normalizeList(res);
        const map = list.reduce((acc, item) => {
          if (item?.TimeUnit_Code && item?.TimeUnit_Moment) {
            acc[Number(item.TimeUnit_Code)] = item.TimeUnit_Moment;
          }
          return acc;
        }, {});
        if (isMounted) setTimeUnitMap(map);
      } catch (error) {
        console.error("Error fetching time units:", error);
      }
    };

    fetchTimeUnits();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSelectQuote = async (quote, subLocationsFromRes) => {
    try {
      setLoadingQuote(true);
      const res =
        subLocationsFromRes !== undefined
          ? { data: { header: quote, subLocations: subLocationsFromRes } }
          : await api.get(`/get-quote/${quote.Doc_Id}`);
      if (res.data.status && res.data.status !== "OK") throw new Error();
      const q = res.data.header || quote || {};
      const subLocations = res.data.subLocations || [];

      // จุดย่อยที่ถูกใช้ในสัญญาอื่นแล้ว (จากใบเสนอราคาเดียวกัน) — เลือกซ้ำไม่ได้ (แก้ไขสัญญาอยู่ให้ exclude สัญญานี้)
      let usedSubLocationIds = [];
      try {
        let url = `/get-quote-used-sub-locations?quote_no=${encodeURIComponent(q.Doc_No || "")}`;
        if (contractId) url += `&exclude_contract_id=${contractId}`;
        const usedRes = await api.get(url);
        if (usedRes.data?.status === "OK" && Array.isArray(usedRes.data.usedSubLocationIds)) {
          usedSubLocationIds = usedRes.data.usedSubLocationIds;
        }
      } catch (_) {}

      const availableSubIds = (subLocations || []).map((s) => s.Sub_Id).filter((id) => id != null && !usedSubLocationIds.includes(id));

      const formatPhone = (tel) => {
        if (!tel) return "";
        const digits = tel.replace(/\D/g, "").slice(0, 10);
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      };

      const formatTaxId = (taxNo) => {
        if (!taxNo) return "";
        const digits = taxNo.replace(/\D/g, "").slice(0, 13);
        if (digits.length < 13) return digits;
        return `${digits[0]}-${digits.slice(1, 5)}-${digits.slice(5,10)}-${digits.slice(10,12)}-${digits[12]}`;
      };

      const startDate = normalizeAD(q.Doc_Date);
      const endDate =
        q.Contract_Period && (q.Contract_Unit != null && q.Contract_Unit !== "")
          ? calcEndDateFromQuotePeriod(startDate, q.Contract_Period, q.Contract_Unit, timeUnitMap)
          : "";

      // ตรวจสอบว่าเป็นใบเสนอราคาใหม่หรือไม่ (Quote_Id เปลี่ยนจากที่มีอยู่แล้ว)
      const isNewQuote = header.Quote_Id != null && header.Quote_Id !== q.Doc_Id;

      setHeader((prev) => ({
        ...prev,
        Quote_Id: q.Doc_Id,
        Quote_No: q.Doc_No,
        AR_Id: q.AR_Id,
        AR_Name: q.AR_Name || "",
        DisplayName: q.DisplayName || "",
        AR_Tel: formatPhone(q.AR_Tel || ""),
        AR_TaxNo: formatTaxId(q.AR_TaxNo || ""),
        Total_Price: q.IsVatIn === 1 ? (q.Total_Price || 0) / 1.07 : (q.Total_Price || 0),
        Discount: q.Discount_Value || 0,
        IsVat: q.IsVat || 0,
        IsVatIn: q.IsVatIn ?? 0,
        Doc_Detail: q.Doc_Detail || "",
        Doc_Remark: q.Doc_Remark || "",
        Contract_Date: startDate,
        Start_Date: startDate,
        End_Date: endDate,
        Contract_Period: q.Contract_Period,
        Contract_Unit: q.Contract_Unit,
        Quote_SubLocations: subLocations,
        Used_SubLocation_Ids: usedSubLocationIds,
        Selected_SubLocation_Ids: availableSubIds.length > 0 ? availableSubIds : (subLocations || []).map((s) => s.Sub_Id).filter(Boolean),
      }));

      // ถ้าเป็นใบเสนอราคาใหม่ ให้ล้างค่าบริการ
      if (isNewQuote && onQuoteChange) {
        onQuoteChange();
      }

      setQuoteModalOpen(false);
      setLoadingQuote(false);
    } catch {
      Swal.fire("ผิดพลาด", "ไม่สามารถโหลดใบเสนอราคา", "error");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 text-[14px] leading-relaxed">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        {showPrintButton && (
          <button
            type="button"
            onClick={onPrint}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            <Icon icon="mdi:printer" className="w-5 h-5" />
            พิมพ์
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
        <h2 className="text-[16px] font-semibold text-gray-700 mb-3">รายละเอียดสัญญา</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        
        <div className="space-y-3">
          {header.Doc_Id ? (
            <div>
              <label className="block text-sm font-medium mb-1">เลขที่สัญญา</label>
              <div
                className="block w-full h-9 border rounded-md px-2 text-[14px] bg-gray-50 text-gray-700 flex items-center"
              >
                {header.Doc_No || ""}
              </div>
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium mb-1">ใบเสนอราคา</label>
            <div className="flex items-center border rounded-md h-9 overflow-hidden">
              {header.Quote_Id ? (
                <Link
                  to={`/quote/edit/${header.Quote_Id}`}
                  state={quoteLinkState || undefined}
                  className="flex-1 h-full px-2 bg-white border-0 outline-none text-blue-600 hover:text-blue-800 hover:underline flex items-center"
                >
                  {header.Quote_No || ""}
                </Link>
              ) : (
                <input
                  type="text"
                  name="Quote_No"
                  value={header.Quote_No || ""}
                  readOnly
                  className="flex-1 h-full px-2 bg-white border-0 outline-none"
                />
              )}
              {!isEdit && (
                <button
                  type="button"
                  onClick={() => setQuoteModalOpen(true)}
                  className="p-2 bg-white hover:bg-gray-100 border-l"
                >
                  <Icon icon="mdi:magnify" className="w-5 h-5 text-gray-500" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">วันที่สัญญา</label>
            <DatePicker
              name="Contract_Date"
              value={header.Contract_Date || ""}
              onChange={(d) =>
                setHeader((prev) => ({
                  ...prev,
                  Contract_Date: d || "",
                }))
              }
              inputClass="flex-1 h-full px-2 border-0 outline-none bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">วันที่เริ่มสัญญา</label>
            <DatePicker
              name="Start_Date"
              value={header.Start_Date || ""}
              onChange={(d) =>
                setHeader((prev) => ({
                  ...prev,
                  Start_Date: d || "",
                }))
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">วันที่สิ้นสุดสัญญา</label>
            <DatePicker
              name="End_Date"
              value={header.End_Date || ""}
              onChange={(d) =>
                setHeader((prev) => ({
                  ...prev,
                  End_Date: d || "",
                }))
              }
            />
          </div>
        </div>

        <div className="space-y-3">

          <div>
            <label className="block text-sm font-medium mb-1">ชื่อที่ใช้ในสัญญา</label>
            <input
              type="text"
              name="DisplayName"
              value={header.DisplayName || ""}
              onChange={handleChange}
              readOnly={isReadOnly}
              disabled={isReadOnly}
              className="w-full h-9 border rounded-md px-2 text-[14px] outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 whitespace-nowrap">เลขประจำตัวผู้เสียภาษี</label>
            <input
              type="text"
              name="AR_TaxNo"
              value={header.AR_TaxNo || ""}
              onChange={(e) => {
                let v = e.target.value.replace(/\D/g, "").slice(0, 13);
                if (v.length > 12)
                  v = `${v[0]}-${v.slice(1, 5)}-${v.slice(5, 10)}-${v.slice(10, 12)}-${v.slice(12)}`;
                else if (v.length > 10)
                  v = `${v[0]}-${v.slice(1, 5)}-${v.slice(5, 10)}-${v.slice(10)}`;
                else if (v.length > 5)
                  v = `${v[0]}-${v.slice(1, 5)}-${v.slice(5)}`;
                else if (v.length > 1)
                  v = `${v[0]}-${v.slice(1)}`;
                setHeader((prev) => ({ ...prev, AR_TaxNo: v }));
              }}
              className="w-full h-11 border rounded-md px-2 text-[14px] outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          {/* สัญญา (radio) */}
          <div>
            <label className="block text-sm font-medium mb-1">สัญญา</label>

            <div className="grid grid-cols-12 items-center mt-1">
              {/*  กลุ่ม radio ไทย/อังกฤษ (3 คอลัมน์) */}
              <div className="col-span-3 flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="Language"
                    value="TH"
                    checked={(header.Language ?? "TH") === "TH"}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    className="h-4 w-4 mr-1"
                  />
                  <span>ไทย</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="radio"
                    name="Language"
                    value="EN"
                    checked={header.Language === "EN"}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    className="h-4 w-4 mr-1"
                  />
                  <span>อังกฤษ</span>
                </label>
              </div>

              {/*  พื้นที่ว่างอีก 9 คอลัมน์ */}
              <div className="col-span-9"></div>
            </div>
          </div>
        </div>

        <div className="flex justify-end items-start mt-[-33px] w-full">
          <div className="space-y-3 w-full max-w-[250px] text-right">
            <h3 className="font-semibold text-[15px] mb-2 text-center">ยอดท้ายบิล</h3>

            <NumberInput
              name="Total_Price"
              label="รวมราคา"
              value={header.Total_Price}
              onValueChange={(name, val) =>
                setHeader((prev) => ({ ...prev, [name]: Number(val) || 0 }))
              }
              isReadOnly={isReadOnly}
            />

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 shrink-0">
                  <input
                    type="checkbox"
                    name="IsVat"
                    checked={header.IsVat === 1}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    className="h-4 w-4"
                  />
                  <span>Vat</span>
                </label>
                <span className="text-xs text-gray-500 text-right flex-1 min-w-0">+7%</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 shrink-0">
                  <input
                    type="checkbox"
                    name="IsVatIn"
                    checked={header.IsVatIn === 1}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    className="h-4 w-4"
                  />
                  <span>Vat in</span>
                </label>
                <span className="text-xs text-gray-500 text-right flex-1 min-w-0">ยอดรวม VAT 7%</span>
              </div>
              <input
                type="text"
                name="VatAmount"
                value={formatWithCommas(header.VatAmount?.toFixed(2))}
                readOnly
                className="w-full h-9 border rounded-md px-2 text-[14px] bg-gray-100 text-right"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-1 text-left">รวมทั้งสิ้น</label>
              <input
                type="text"
                name="GrandTotal"
                value={formatWithCommas(header.GrandTotal?.toFixed(2))}
                readOnly
                className="w-full h-9 border rounded-md px-2 text-[14px] bg-gray-100 text-right font-bold"
              />
            </div>
          </div>
        </div>

      </div>

      <div className="mt-6 grid grid-cols-12 gap-4">

        <div className="col-span-12 md:col-span-6 border border-blue-300 rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-md font-semibold text-blue-800">ข้อมูลที่ใช้ในสัญญา</h3>
            {onEditContractLocation && header.Contract_ServiceAddr_Id && !isReadOnly && (
              <button
                type="button"
                onClick={onEditContractLocation}
                className="flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900 border border-blue-300 rounded px-2 py-1 hover:bg-blue-50"
              >
                <Icon icon="mdi:pencil" className="w-4 h-4" />
                แก้ไข
              </button>
            )}
          </div>

          <div className="grid grid-cols-12 gap-4">

            <div className="col-span-12 md:col-span-4">
              <label className="block font-medium mb-1 whitespace-nowrap">ชื่อสถานที่ใช้ในสัญญา</label>
              <input
                type="text"
                value={header.Contract_ServiceAddr_Name || ""}
                readOnly
                className="w-full h-9 border rounded-md px-2 bg-gray-100"
              />
            </div>

            <div className="col-span-12 md:col-span-8">
              <label className="block font-medium mb-1 whitespace-nowrap">ที่อยู่ที่ใช้ในสัญญา</label>
              <div className="flex items-center border rounded-md h-9 bg-gray-100 overflow-hidden">
                <input
                  type="text"
                  value={header.Contract_ServiceAddr_FullText || ""}
                  readOnly
                  disabled
                  className="flex-1 h-full px-2 bg-gray-100 text-gray-700 outline-none"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!header.AR_Id)
                      return Swal.fire("ยังไม่มีลูกค้า", "เลือกก่อน", "warning");
                    setChoosePurpose("3");
                    setServiceModalOpen(true);
                  }}
                  className="p-2 bg-white border-l hover:bg-gray-100"
                >
                  <Icon icon="mdi:map-marker" className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="col-span-12 md:col-span-4">
              <label className="block font-medium mb-1">ชื่อผู้ติดต่อ</label>
              <input
                type="text"
                value={header.Contract_ContactName || ""}
                readOnly
                className="w-full h-9 border rounded-md px-2 bg-gray-100"
              />
            </div>

            <div className="col-span-12 md:col-span-4">
              <label className="block font-medium mb-1">เบอร์โทรศัพท์</label>
              <input
                type="text"
                value={header.Contract_ContactTel || ""}
                readOnly
                className="w-full h-9 border rounded-md px-2 bg-gray-100"
              />
            </div>

          </div>
        </div>

        <div className="col-span-12 md:col-span-6 border border-green-300 rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-md font-semibold text-green-800">สถานที่บริการ</h3>
            {onEditServiceLocation && header.ServiceAddr_Id && !isReadOnly && (
              <button
                type="button"
                onClick={onEditServiceLocation}
                className="flex items-center gap-1 text-sm text-green-700 hover:text-green-900 border border-green-300 rounded px-2 py-1 hover:bg-green-50"
              >
                <Icon icon="mdi:pencil" className="w-4 h-4" />
                แก้ไข
              </button>
            )}
          </div>

          <div className="grid grid-cols-12 gap-4">

            <div className="col-span-12 md:col-span-4">
              <label className="block font-medium mb-1">ชื่อสถานที่บริการ</label>
              <input
                type="text"
                value={header.ServiceAddr_Name || ""}
                readOnly
                className="w-full h-9 border rounded-md px-2 bg-gray-100"
              />
            </div>

            <div className="col-span-12 md:col-span-8">
              <label className="block font-medium mb-1 whitespace-nowrap">ที่อยู่สถานที่บริการ</label>
              <div className="flex items-center border rounded-md h-9 bg-gray-100 overflow-hidden">
                <input
                  type="text"
                  value={header.ServiceAddr_FullText || ""}
                  readOnly
                  disabled
                  className="flex-1 h-full px-2 bg-gray-100 text-gray-700 outline-none"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!header.AR_Id)
                      return Swal.fire("ยังไม่มีลูกค้า", "เลือกก่อน", "warning");
                    setChoosePurpose("2");
                    setServiceModalOpen(true);
                  }}
                  className="p-2 bg-white border-l hover:bg-gray-100"
                >
                  <Icon icon="mdi:map-marker" className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="col-span-12 md:col-span-4">
              <label className="block font-medium mb-1">ชื่อผู้ติดต่อ</label>
              <input
                type="text"
                value={header.Service_ContactName || ""}
                readOnly
                className="w-full h-9 border rounded-md px-2 bg-gray-100"
              />
            </div>

            <div className="col-span-12 md:col-span-4">
              <label className="block font-medium mb-1">เบอร์โทรศัพท์</label>
              <input
                type="text"
                value={header.Service_ContactTel || ""}
                readOnly
                className="w-full h-9 border rounded-md px-2 bg-gray-100"
              />
            </div>

          </div>
        </div>

      </div>

      {/* จุดย่อยสถานที่บริการ (จากใบเสนอราคา) - แสดงแบบอ่านอย่างเดียว + ติ๊กเลือกว่าจะใช้จุดไหน */}
      {header.Quote_SubLocations && header.Quote_SubLocations.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 mt-6 border border-gray-300 text-[14px]">
          <h3 className="font-bold text-gray-700 text-lg mb-4">จุดย่อยสถานที่บริการ (จากใบเสนอราคา)</h3>
          <p className="text-sm text-gray-600 mb-3">ติ๊กเลือกจุดย่อยที่ต้องการใช้ในสัญญา (จุดที่ใช้ในสัญญาอื่นแล้วจะเลือกซ้ำไม่ได้)</p>
          <div className="overflow-x-auto rounded-lg border border-gray-300">
            <table className="table-fixed w-full border border-gray-300 text-[14px]">
              <thead>
                <tr>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[6%] py-2">ลำดับ</th>
                  <th className="bg-[#E13F57] text-white text-left border border-gray-300 w-[18%] py-2">ชื่อจุดย่อย</th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[7%] py-2">จำนวน</th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[13%] py-2">ราคาต่อหน่วย</th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[10%] py-2">ส่วนลด</th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[12%] py-2">รวม</th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[10%] py-2">รูป</th>
                  <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[6%] py-2">ใช้ในสัญญา</th>
                </tr>
              </thead>
              <tbody>
                {header.Quote_SubLocations.map((sub, index) => {
                  const net = (Number(sub.Qty) || 0) * (Number(sub.Price_Unit) || 0) - (Number(sub.Discount) || 0);
                  const total = net;
                  const selectedIds = header.Selected_SubLocation_Ids || [];
                  const usedIds = header.Used_SubLocation_Ids || [];
                  const isUsed = usedIds.includes(sub.Sub_Id);
                  const isSelected = selectedIds.includes(sub.Sub_Id);
                  let subImgSrc = sub.Sub_Img_Url || null;
                  if (!subImgSrc && sub.Sub_Img?.data) {
                    try {
                      subImgSrc = URL.createObjectURL(new Blob([new Uint8Array(sub.Sub_Img.data)], { type: "image/*" }));
                    } catch (_) {}
                  }
                  return (
                    <tr key={sub.Sub_Id || index} className={`odd:bg-white even:bg-gray-50 hover:bg-gray-100 ${isUsed ? "bg-amber-50" : ""}`}>
                      <td className="text-center border border-gray-300 py-2">{index + 1}</td>
                      <td className="border border-gray-300 px-2 py-2 font-medium">{sub.Sub_Name || "-"}</td>
                      <td className="border border-gray-300 px-2 py-2 text-right">
                        {Number(sub.Qty) != null && sub.Qty !== "" ? Number(sub.Qty).toLocaleString("th-TH") : "-"}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-right">
                        {Number(sub.Price_Unit) != null && sub.Price_Unit !== "" ? Number(sub.Price_Unit).toLocaleString("th-TH", { minimumFractionDigits: 2 }) : "-"}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-right">
                        {Number(sub.Discount) != null && sub.Discount !== "" ? Number(sub.Discount).toLocaleString("th-TH", { minimumFractionDigits: 2 }) : "-"}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-right font-medium">
                        {total.toLocaleString("th-TH", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-center">
                        {subImgSrc ? (
                          <img src={subImgSrc} alt="" className="w-[50px] h-[50px] object-cover rounded-md mx-auto" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-center">
                        {isUsed ? (
                          <span className="text-amber-700 text-xs font-medium">ใช้แล้ว</span>
                        ) : !isReadOnly ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setHeader((prev) => {
                                const ids = prev.Selected_SubLocation_Ids || [];
                                const next = ids.includes(sub.Sub_Id)
                                  ? ids.filter((id) => id !== sub.Sub_Id)
                                  : [...ids, sub.Sub_Id];
                                return { ...prev, Selected_SubLocation_Ids: next };
                              });
                            }}
                            className="w-4 h-4 text-[#E13F57] border-gray-300 rounded focus:ring-[#E13F57]"
                          />
                        ) : (
                          isSelected ? "✓" : "-"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-4">
        <div>
          <label className="block text-sm font-medium mb-1 whitespace-nowrap">รายละเอียดเอกสารตามใบเสนอราคา</label>
          <textarea
            name="Doc_Detail"
            value={header.Doc_Detail || ""}
            onChange={handleChange}
            readOnly={isReadOnly}
            disabled={isReadOnly}
            rows={3}
            className="w-full border rounded-md px-3 py-2 text-[14px] focus:ring-2 focus:ring-orange-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 whitespace-nowrap">หมายเหตุเอกสารตามใบเสนอราคา</label>
          <textarea
            name="Doc_Remark"
            value={header.Doc_Remark || ""}
            onChange={handleChange}
            readOnly={isReadOnly}
            disabled={isReadOnly}
            rows={3}
            className="w-full border rounded-md px-3 py-2 text-[14px] focus:ring-2 focus:ring-orange-400"
          />
        </div>
      </div>

      </div>

      {!isEdit && quoteModalOpen && (
        <QuoteModal
          isOpen={quoteModalOpen}
          onClose={() => setQuoteModalOpen(false)}
          onSelect={handleSelectQuote}
          selectedQuoteId={header.Quote_Id}
        />
      )}

      {serviceModalOpen && (
        <ServiceLocationModal
          isOpen={serviceModalOpen}
          onClose={() => setServiceModalOpen(false)}
          arId={header.AR_Id}
          purpose={choosePurpose || "2"} // ส่ง purpose ไปยัง modal (2 = สถานที่บริการ, 3 = ที่อยู่ตามสัญญา)
          locations={
            choosePurpose === "3"
              ? (contractLocation ? [contractLocation] : [])
              : serviceLocations
          }

          onSelect={(loc, needsReload = false) => {
            if (choosePurpose === "3") {
              setHeader((prev) => ({
                ...prev,
                Contract_ServiceAddr_Id: loc.ServiceAddr_Id,
                Contract_ServiceAddr_Name: loc.ServiceAddr_Name,
                Contract_ServiceAddr_FullText: loc.ServiceAddr_FullName,
                Contract_ContactName: loc.AR_ContactName || "",
                Contract_ContactTel: loc.AR_Tel || "",
              }));
            } else {
              setHeader((prev) => ({
                ...prev,
                ServiceAddr_Id: loc.ServiceAddr_Id,
                ServiceAddr_Name: loc.ServiceAddr_Name,
                ServiceAddr_FullText: loc.ServiceAddr_FullName,
                Service_ContactName: loc.AR_ContactName || "",
                Service_ContactTel: loc.AR_Tel || "",
              }));
            }
            setServiceModalOpen(false);
            
            // Refresh service addresses if needed (e.g., after creating new address)
            if (needsReload && setReload) {
              setReload(true);
            }
          }}
        />
      )}
    </div>
  );
}
